import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { usePools } from '@/hooks/use-pools'
import { usePoolsBySystem, useSystemHistory } from '@/hooks/use-systems'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Layers, Loader2, Copy, Check, ArrowRight, ArrowLeft, Search, Clock, Plus, Trash2, Zap } from 'lucide-react'
import { getPoolIcon, SystemIcon } from '@/lib/entity-icons'
import { useIncognito } from '@/hooks/use-incognito'
import { formatDateTime } from '@/lib/utils'
import AddToPoolDialog from '@/components/systems/AddToPoolDialog'
import SystemIdCombobox from '@/components/systems/SystemIdCombobox'
import SystemsAnalytics from '@/components/systems/SystemsAnalytics'
import ChargingSessionsView from '@/components/monitoring/ChargingSessionsView'
import { type TimeRange } from '@/hooks/use-monitoring'

// ── Pools tab ────────────────────────────────────────────────────────────────

function PoolsTab({ systemID }: { systemID: string }) {
  const { data: ownerPoolIDs, isLoading, isError, error } = usePoolsBySystem(systemID)
  const { data: allPools = [] } = usePools()
  const incognito = useIncognito()
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [showAddDialog, setShowAddDialog] = useState(false)

  const handleCopyId = async (e: React.MouseEvent, id: string) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(id)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // ignore
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <SystemIcon className="h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">System not found</h3>
          <p className="text-sm text-muted-foreground">{error?.message ?? 'Could not look up this system ID.'}</p>
        </CardContent>
      </Card>
    )
  }

  if (!ownerPoolIDs || ownerPoolIDs.length === 0) {
    return (
      <>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <SystemIcon className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No pools found</h3>
            <p className="text-sm text-muted-foreground mb-4">This system does not belong to any pool.</p>
            <Button size="sm" onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add to Pool
            </Button>
          </CardContent>
        </Card>
        <AddToPoolDialog
          systemID={systemID}
          currentPoolIDs={[]}
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
        />
      </>
    )
  }

  const ownerPools = ownerPoolIDs.map(
    (id) => allPools.find((p) => p.poolID === id) ?? { poolID: id, name: id, systemCount: undefined }
  )

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Found in <span className="font-medium text-foreground">{ownerPoolIDs.length}</span> pool{ownerPoolIDs.length !== 1 ? 's' : ''}
        </p>
        <Button size="sm" onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add to Pool
        </Button>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {ownerPools.map((pool) => (
          <Card key={pool.poolID} className="h-full hover:border-primary/50 transition-colors">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                {(() => { const PoolIcon = getPoolIcon(pool); return <PoolIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> })()}
                <span className="truncate">{incognito.poolName(pool.name)}</span>
              </CardTitle>
              <CardDescription className="font-mono text-xs flex items-center gap-2">
                {!incognito.isIncognito && (
                  <>
                    <span className="truncate">{pool.poolID}</span>
                    <button
                      onClick={(e) => handleCopyId(e, pool.poolID)}
                      className="flex-shrink-0 p-1 hover:bg-muted rounded transition-colors"
                      title="Copy Pool ID"
                    >
                      {copiedId === pool.poolID ? (
                        <Check className="h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                      )}
                    </button>
                  </>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Link
                to={`/pools/${pool.poolID}`}
                className="flex items-center justify-between gap-2 rounded px-2 py-1.5 text-sm text-primary hover:bg-muted transition-colors group"
              >
                <span>View pool</span>
                <ArrowRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            </CardContent>
          </Card>
        ))}
      </div>
      <AddToPoolDialog
        systemID={systemID}
        currentPoolIDs={ownerPoolIDs}
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />
    </div>
  )
}

// ── History tab ───────────────────────────────────────────────────────────────

function HistoryTab({ systemID }: { systemID: string }) {
  const { data: events = [], isLoading } = useSystemHistory(systemID)
  const { data: pools = [] } = usePools()
  const incognito = useIncognito()

  const getPoolName = (poolID: string) => {
    const pool = pools.find((p) => p.poolID === poolID)
    const name = pool?.name || poolID
    return pool ? incognito.poolName(name) : incognito.uuid(poolID)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Clock className="h-12 w-12 mb-4 opacity-50" />
        <p>No history available for this system</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Timeline */}
      <div className="relative border-l-2 border-muted pl-6 space-y-6">
        {events.map((event, index) => (
          <div key={index} className="relative">
            <div
              className={`absolute -left-[27px] w-4 h-4 rounded-full border-2 ${
                event.eventType === 'INSERT'
                  ? 'bg-green-500 border-green-500'
                  : 'bg-red-500 border-red-500'
              }`}
            />
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {event.eventType === 'INSERT' ? (
                      <Plus className="h-4 w-4 text-green-500" />
                    ) : (
                      <Trash2 className="h-4 w-4 text-red-500" />
                    )}
                    <span className="font-medium">
                      {event.eventType === 'INSERT' ? 'Added to pool' : 'Removed from pool'}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-foreground">{getPoolName(event.poolID)}</p>
                  {!incognito.isIncognito && (
                    <p className="text-xs text-muted-foreground font-mono mt-1">{event.poolID}</p>
                  )}
                </div>
                <p className="text-sm text-muted-foreground whitespace-nowrap">
                  {formatDateTime(event.timestamp)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Summary */}
      <div className="pt-4 border-t">
        <div className="grid grid-cols-2 gap-4 text-sm max-w-xs">
          <div>
            <p className="text-muted-foreground">Total Events</p>
            <p className="font-medium text-lg">{events.length}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Pools Visited</p>
            <p className="font-medium text-lg">{new Set(events.map((e) => e.poolID)).size}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Charging tab ─────────────────────────────────────────────────────────────

function ChargingTab({ systemID }: { systemID: string }) {
  const [timeRange, setTimeRange] = useState<TimeRange>('7d')

  return (
    <div className="h-[calc(100vh-340px)] min-h-96 border rounded-lg overflow-hidden">
      <ChargingSessionsView
        lockedSystemId={systemID}
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
      />
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function SystemsPage() {
  const { systemID: paramSystemID, tab } = useParams<{ systemID?: string; tab?: string }>()
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const { data: pools = [], isLoading: poolsLoading } = usePools()
  const { data: systemPoolIDs = [], isLoading: systemPoolsLoading } = usePoolsBySystem(paramSystemID ?? '')

  const showChargingTab =
    systemPoolsLoading ||
    poolsLoading ||
    systemPoolIDs.some((id) => {
      const pool = pools.find((p) => p.poolID === id)
      return pool?.controlConfig?.purpose === 'intraday' || pool?.controlConfig?.purpose === 'intraday_cloud'
    })

  const activeTab = tab === 'charging' && !showChargingTab ? 'pools' : (tab ?? 'pools')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = inputValue.trim()
    if (!trimmed) return
    navigate(`/systems/${encodeURIComponent(trimmed)}`)
  }

  const handleTabChange = (value: string) => {
    if (!paramSystemID) return
    navigate(`/systems/${encodeURIComponent(paramSystemID)}/${value}`)
  }

  if (paramSystemID) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/systems')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <h1 className="text-3xl font-bold tracking-tight font-mono">
            {decodeURIComponent(paramSystemID)}
          </h1>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="pools">
              <Layers className="h-4 w-4 mr-2" />
              Pools
            </TabsTrigger>
            <TabsTrigger value="history">
              <Clock className="h-4 w-4 mr-2" />
              History
            </TabsTrigger>
            {showChargingTab && (
              <TabsTrigger value="charging">
                <Zap className="h-4 w-4 mr-2" />
                Charging
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="pools" className="mt-4">
            <PoolsTab systemID={decodeURIComponent(paramSystemID)} />
          </TabsContent>

          <TabsContent value="history" className="mt-4">
            <HistoryTab systemID={decodeURIComponent(paramSystemID)} />
          </TabsContent>

          {showChargingTab && (
            <TabsContent value="charging" className="mt-4">
              <ChargingTab systemID={decodeURIComponent(paramSystemID)} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Systems</h1>
        <p className="text-muted-foreground">Explore system analytics or look up a specific system</p>
      </div>

      <form onSubmit={handleSearch} className="flex gap-2 max-w-xl">
        <SystemIdCombobox
          value={inputValue}
          onChange={setInputValue}
          onSubmit={(id) => navigate(`/systems/${encodeURIComponent(id)}`)}
          placeholder="Enter system ID…"
        />
        <Button type="submit" disabled={!inputValue.trim()}>
          <Search className="h-4 w-4 mr-2" />
          Search
        </Button>
      </form>

      <SystemsAnalytics pools={pools} poolsLoading={poolsLoading} />
    </div>
  )
}
